import React, { useEffect, useState } from 'react';
import API from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import PageHeader from '../components/PageHeader';
import DataTable from '../components/DataTable';
import Modal from '../components/Modal';
import Icon from '../components/Icon';
import { FormField, Input, Textarea, SubmitBtn } from '../components/FormField';

const emptyForm = { name:'', email:'', username:'', password:'', phone:'', address:'', birth_date:'', hire_date:'', subject_ids:[] };

export default function Teachers() {
  const { t, lang } = useLanguage();
  const tSubj = (n) => lang === 'en' ? t('subjectNames.' + n, n) : n;
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    const [te, s] = await Promise.all([API.get('/teachers'), API.get('/subjects')]);
    setTeachers(te.data); setSubjects(s.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const openEdit = async row => {
    const { data } = await API.get(`/teachers/${row.id}`);
    // hire_date API-dən ISO kimi gəlir — state-də yalnız YYYY-MM-DD saxlanılır
    setForm({ ...emptyForm, ...data, hire_date: (data.hire_date || '').split('T')[0],
              password:'', subject_ids: data.subject_list?.map(s=>s.id)||[] });
    setError(''); setModal('edit');
  };

  const handleSave = async e => {
    e.preventDefault(); setSaving(true); setError('');
    try {
      if (modal==='add') await API.post('/teachers', form);
      else await API.put(`/teachers/${form.id}`, form);
      setModal(null); load();
    } catch (err) { setError(err.response?.data?.message||t('common.error')); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm(t('teachers.confirmDelete'))) return;
    await API.delete(`/teachers/${id}`); load();
  };

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleSubject = id => {
    setForm(p => ({
      ...p,
      subject_ids: p.subject_ids.includes(id)
        ? p.subject_ids.filter(s=>s!==id)
        : [...p.subject_ids, id]
    }));
  };

  const columns = [
    { header: '#', render: (_, i) => i+1, width:'40px' },
    { header: t('common.fullName'), render: r => r.name },
    { header: t('common.email'), accessor: 'email' },
    /* Siyahıda təkcə fənn müəllimləri yoxdur — koordinator, assistent,
       tərbiyəçi, məşqçi də pedaqoji heyətdədir. Vəzifə sütunu onları
       bir-birindən ayırır. */
    { header: t('common.position'), render: r => (
      <span style={{ fontSize:12, color:'#64748b' }}>{r.position || '—'}</span>
    )},
    { header: t('common.phone'), render: r => r.phone || r.u_phone },
    { header: t('teachers.colSubjects'), render: r => (
      <span style={{ fontSize:12, color:'#666' }}>{r.subjects ? r.subjects.split(',').map(s => tSubj(s.trim())).join(', ') : '—'}</span>
    )},
    { header: t('common.status'), render: r => (
      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:12, fontWeight:600,
        background: r.is_active ? '#e8f8ee':'#fdecea', color: r.is_active?'#27ae60':'#e74c3c' }}>
        {r.is_active ? t('common.active'):t('common.inactive')}
      </span>
    )},
    { header: t('common.actions'), render: r => (
      <div style={{ display:'flex', gap:6 }}>
        <button className="btn btn-sm btn-secondary" onClick={()=>openEdit(r)}><Icon name="edit" size={15} /></button>
        <button className="btn btn-sm btn-danger" onClick={()=>handleDelete(r.id)}><Icon name="trash" size={15} /></button>
      </div>
    )}
  ];

  return (
    <div>
      {/* "Müəllim əlavə et" düyməsi götürüldü: müəllim qeydi artıq
          İstifadəçilər bölməsində yaradılır və siyahıya özü düşür.
          İki yerdən əlavə etmək eyni adamın iki qeydini yaradırdı. */}
      <PageHeader icon="teachers" title={t('teachers.titleEmoji')} subtitle={`${t('common.total')} ${teachers.length} ${t('teachers.countSuffix')}`} />
      <DataTable columns={columns} data={teachers} loading={loading} />

      {modal && (
        <Modal title={modal==='add'?t('teachers.modalNew'):t('teachers.modalEdit')} onClose={()=>setModal(null)} size="lg">
          <form onSubmit={handleSave}>
            {error && <div className="form-error-box">{error}</div>}
            <div className="form-row">
              <FormField label={t('common.fullName')} required><Input value={form.name} onChange={e=>f('name',e.target.value)} required /></FormField>
              <FormField label={t('common.username')} required><Input value={form.username} onChange={e=>f('username',e.target.value)} required /></FormField>
            </div>
            <div className="form-row">
              <FormField label={t('common.email')}><Input type="email" autoComplete="off" name="contact-email" value={form.email} onChange={e=>f('email',e.target.value)} /></FormField>
              <FormField label={modal==='add'?t('common.password'):t('teachers.fieldPasswordChange')}>
                <Input type="password" autoComplete="new-password" name="new-pass" value={form.password} onChange={e=>f('password',e.target.value)} placeholder={modal==='edit'?t('teachers.fieldPasswordPlaceholder'):''}/>
              </FormField>
            </div>
            <div className="form-row">
              <FormField label={t('common.phone')}><Input value={form.phone} onChange={e=>f('phone',e.target.value)} /></FormField>
              <FormField label={t('teachers.fieldHireDate')}><Input type="date" value={form.hire_date || ''} onChange={e=>f('hire_date',e.target.value)} /></FormField>
            </div>
            <FormField label={t('teachers.fieldSubjects')}>
              <div className="subject-checkboxes">
                {subjects.map(s => (
                  <label key={s.id} className="checkbox-label">
                    <input type="checkbox" checked={form.subject_ids.includes(s.id)} onChange={()=>toggleSubject(s.id)} />
                    {tSubj(s.name)}
                  </label>
                ))}
              </div>
            </FormField>
            <FormField label={t('common.address')}><Textarea value={form.address} onChange={e=>f('address',e.target.value)} /></FormField>
            <div className="form-actions">
              <button type="button" className="btn btn-secondary" onClick={()=>setModal(null)}>{t('common.cancel')}</button>
              <SubmitBtn loading={saving}>{modal==='add'?t('common.add'):t('common.save')}</SubmitBtn>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
